import type { HotelsContainer } from '@elara/client-core/containers/HotelsContainer';
import type { HotelMediaGallery } from '@elara/client-core/models/HotelMediaModel';
import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import { HOTEL_MEDIA_GALLERY_DOC_ID } from '@elara/domain/models/HotelMediaModelSpec';

import { useApp } from '#states/hooks.js';

interface FeaturesShape {
  hotels?: HotelsContainer;
}

/**
 * Reactively loads a hotel's FULL photo gallery (`hotels/{hotelId}/media/gallery`) in
 * card-compatible shape. Returns undefined until loaded (or when the hotel has no stored
 * gallery) — callers fall back to the head images on the hotel doc.
 *
 * Gallery docs are large (a hotel can have 100+ images), so pass `enabled: false` until the
 * user shows intent to browse (hover/touch); a dedicated hotel view can load eagerly.
 */
export function useHotelGallery(hotelId: string | undefined, enabled = true): WidgetImage[] | undefined {
  const { features } = useApp('features');
  const { hotels } = features as FeaturesShape;

  const handle =
    enabled && hotelId && hotels
      ? hotels.ClientHotelMedia.get({ hotelId, id: HOTEL_MEDIA_GALLERY_DOC_ID })
      : undefined;
  const gallery = handle?.data as HotelMediaGallery | undefined;
  const images = gallery?.widgetImages;
  return images && images.length > 0 ? images : undefined;
}
